import React, { useState, useEffect } from "react";
import { Button, Box, Menu, MenuItem } from "@mui/material";
import { IoCalendarOutline } from "react-icons/io5";
import dayjs from "dayjs";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const DateRangePickerButton = ({ selectedPeriod, onPeriodChange, direction, t }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const { startDate, endDate } = selectedPeriod || {};
    const [selectedDateRange, setSelectedDateRange] = useState([startDate, endDate]);
    const [customRange, setCustomRange] = useState(false);
    useEffect(() => {
        // Sync internal state with parent's selectedPeriod
        setSelectedDateRange([startDate, endDate]);
    }, [startDate, endDate]);

    // Open/Close Menu Handlers
    const handleOpenMenu = (event) => setAnchorEl(event.currentTarget);
    const handleCloseMenu = () => {
        setAnchorEl(null);
        setCustomRange(false); // Reset custom range
    };

    const handleRangeSelect = (range) => {
        setSelectedDateRange(range);
        let startDate, endDate;
        const today = dayjs();

        if (range === "Custom Range") {
            setCustomRange(true);
        } else {
            switch (range) {
                case t.Today:
                    startDate = today;
                    endDate = today;
                    break;
                case t.Yesterday:
                    startDate = today.subtract(1, "day");
                    endDate = today.subtract(1, "day");
                    break;
                case t.Last7Days:
                    startDate = today.subtract(7, "days");
                    endDate = today;
                    break;
                case t.Last30Days:
                    startDate = today.subtract(30, "days");
                    endDate = today;
                    break;
                case t.ThisMonth:
                    startDate = today.startOf("month");
                    endDate = today.endOf("month");
                    break;
                case t.LastMonth:
                    const lastMonth = today.subtract(1, "month");
                    startDate = lastMonth.startOf("month");
                    endDate = lastMonth.endOf("month");
                    break;
                default:
                    break;
            }

            if (startDate && endDate) {
                setSelectedDateRange([startDate.toDate(), endDate.toDate()]); // Converting to JavaScript Date objects
                onPeriodChange({
                    startDate: startDate.toDate(),
                    endDate: endDate.toDate(),
                });
            }
        }
        handleCloseMenu();
    };

    const handleDateChange = (dates) => {
        const [startDate, endDate] = dates;
        setSelectedDateRange([startDate, endDate]);
        if (startDate && endDate) {
            onPeriodChange({
                startDate: startDate,
                endDate: endDate
            });
        }
    };

    const formatDateRange = () => {
        if (selectedDateRange[0] && selectedDateRange[1]) {
            return `${dayjs(selectedDateRange[0]).format("DD/MM/YYYY")} - ${dayjs(selectedDateRange[1]).format("DD/MM/YYYY")}`;
        }
        return t.orderTable_filters_selectDateRange;
    };

    const handleCustomRangeHover = () => {
        setCustomRange(true); // Show custom range picker
    };

    const handleCustomRangeClick = (event) => {
        event.stopPropagation(); // Prevent menu from closing
        setCustomRange(true); // Show custom range picker
    };

    return (
        <Box>
            {/* Main Button */}
            <Button
                variant="outlined"
                onClick={handleOpenMenu}
                sx={{
                    color: "var(--text-color)",
                    borderColor: "var(--primary-color)",
                    backgroundColor: "var(--secondary-color)",
                    textTransform: "none",
                    fontSize: { md: "16px", xs: "13px" },
                    fontWeight: 600,
                    padding: { md: "10px 20px", xs: "12px" },
                    borderRadius: "12px",
                    display: "flex",
                    alignItems: "center",
                    transition: "all 0.3s ease",
                    "&:hover": {
                        backgroundColor: "var(--primary-color-light)",
                        borderColor: "var(--primary-color-dark)",
                        color: "#fff",
                    },
                }}
            >
                <span>{formatDateRange()}</span>
                <IoCalendarOutline style={{ marginLeft: "10px", fontSize: "18px", marginRight: "10px" }} />
            </Button>

            {/* Dropdown Menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleCloseMenu}
                sx={{
                    "& .MuiPaper-root": {
                        padding: "16px",
                        width: "300px",
                        borderRadius: "8px",
                        boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
                    },
                }}
            >
                {[t.Today, t.Yesterday, t.Last7Days, t.Last30Days, t.ThisMonth,t.LastMonth].map(
                    (range) => (
                        <MenuItem
                            key={range}
                            selected={selectedDateRange === range}
                            onClick={() => handleRangeSelect(range)}

                            sx={{
                                "&.Mui-selected": {
                                    backgroundColor: "var(--primary-color)",
                                    color: "#fff",
                                },
                            }}
                        >
                            {range}
                        </MenuItem>
                    )
                )}
                <MenuItem
                    key="Custom Range"
                    selected={selectedDateRange === "Custom Range"}
                    onClick={handleCustomRangeClick} // Handle click
                    onMouseEnter={handleCustomRangeHover} // Handle hover
                    sx={{
                        "&.Mui-selected": {
                            backgroundColor: "var(--primary-color)",
                            color: "#fff",
                        },
                    }}
                >
                   {t.customPeriod}
                </MenuItem>

                {/* Custom Range Picker (only show if customRange is true) */}
                {customRange && (
                    <Box sx={{ marginTop: "16px" }}>
                        <DatePicker
                            selected={selectedDateRange[0]} // Corrected: Access startDate from selectedDateRange
                            onChange={handleDateChange}
                            startDate={selectedDateRange[0]} // Corrected: Access startDate from selectedDateRange
                            endDate={selectedDateRange[1]} // Corrected: Access endDate from selectedDateRange
                            selectsRange
                            inline
                        />

                    </Box>
                )}
            </Menu>
        </Box>
    );
};

export default DateRangePickerButton;
